import React, { useState, useEffect } from 'react';
import {
  HelpCircle, ChevronDown, Mail, PhoneCall,
  Headphones, Sparkles, ArrowRight, LifeBuoy, Loader2,
  ShieldCheck, Clock, Zap
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { faqService } from '../services/faqService';
import { contactService } from '../services/contactService';
import { Helmet } from 'react-helmet-async';

const FaqSection = ({ isFullPage = false }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(0); // first item open by default
  const [phone, setPhone] = useState('+91 98765 43210');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await faqService.getFaqs();
        setFaqs(data.data || []);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
      } finally {
        setLoading(false);
      }

      try {
        const contactRes = await contactService.getContactDetails();
        const contactPhone = contactRes?.data?.phone || contactRes?.phone;
        if (contactPhone) {
          setPhone(contactPhone);
        }
      } catch (err) {
        console.error('Error fetching contact details in FaqSection:', err);
      }
    };

    fetchData();
  }, []);

  const toggleAccordion = (index) => {
    setOpenIndex(prevIndex => prevIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div className="container py-5 d-flex justify-content-center align-items-center">
        <Loader2 className="animate-spin text-primary" size={36} />
      </div>
    );
  }

  // Display top 5 on homepage if not full page
  const displayFaqs = isFullPage ? faqs : faqs.slice(0, 5);

  return (
    <>
      <section className="py-4 py-md-5 bg-white position-relative">
        <div className="container">

          {/* Section Header */}
          <div className="text-center mb-4 mb-md-5" style={{ maxWidth: '680px', margin: '0 auto' }}>
            <div className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary small fw-bold mb-2.5">
              <HelpCircle size={15} />
              <span>HELP & FAQ</span>
            </div>
            <h2 className="fw-black text-dark mb-2 fs-3 fs-md-2" style={{ letterSpacing: '-0.5px' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-muted small fs-md-6 mb-0">
              Quick answers to questions about booking stays, travel packages, cancellations, and check-ins.
            </p>
          </div>

          {/* Modern Accordion List */}
          <div className="faq-accordion-wrapper mb-5" style={{ maxWidth: '820px', margin: '0 auto' }}>
            {displayFaqs.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {displayFaqs.map((faq, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div
                      key={faq.id || index}
                      className={`card border rounded-4 overflow-hidden bg-white faq-item-card transition ${isOpen ? 'border-primary border-opacity-50 shadow-sm' : 'border-light-subtle'
                        }`}
                    >
                      {/* Question Button */}
                      <button
                        onClick={() => toggleAccordion(index)}
                        className="btn w-100 p-3.5 p-md-4 text-start bg-transparent border-0 d-flex justify-content-between align-items-center gap-3"
                        aria-expanded={isOpen}
                      >
                        <span className={`fw-bold text-dark fs-6 lh-base mb-0 transition ${isOpen ? 'text-primary' : ''}`}>
                          {faq.question}
                        </span>
                        <div
                          className={`chevron-circle flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle transition ${isOpen ? 'bg-primary text-white rotate-180' : 'bg-light text-secondary'
                            }`}
                          style={{ width: '32px', height: '32px' }}
                        >
                          <ChevronDown size={18} />
                        </div>
                      </button>

                      {/* Answer Panel */}
                      <div
                        className={`faq-collapse transition ${isOpen ? 'faq-expanded' : 'faq-collapsed'}`}
                        style={{
                          maxHeight: isOpen ? '500px' : '0',
                          opacity: isOpen ? 1 : 0,
                          overflow: 'hidden',
                          transition: 'max-height 0.35s ease-in-out, opacity 0.3s ease-in-out'
                        }}
                      >
                        <div className="px-3.5 pb-3.5 px-md-4 pb-md-4 pt-1 text-secondary lh-base border-top border-light-subtle" style={{ fontSize: '14.5px' }}>
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-5 bg-light rounded-4">
                <LifeBuoy size={40} className="text-muted mb-2" />
                <p className="text-muted mb-0">No FAQs available at the moment.</p>
              </div>
            )}
          </div>

          {/* SEO JSON-LD schema */}
          {faqs.length > 0 && (
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.question,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": faq.answer
                    }
                  }))
                })}
              </script>
            </Helmet>
          )}

        </div>
      </section>

      {/* ── Enterprise 24/7 Concierge Support CTA (Full Width & Minimal Premium) ── */}
      <section
        className="faq-cta-fullwidth position-relative overflow-hidden py-4 py-md-5 w-100"
        style={{
          background: 'linear-gradient(180deg, #090e1d 0%, #0d1733 100%)',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <div className="container position-relative py-2">
          <div className="row align-items-center justify-content-between gy-4">

            {/* Left Content Column */}
            <div className="col-12 col-lg-7 text-center text-lg-start">

              {/* Minimal Live Status Tag */}
              <div
                className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-3"
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.25)'
                }}
              >
                <span className="live-radar-dot"></span>
                <span style={{ color: '#34d399', fontSize: '12px', fontWeight: '600', letterSpacing: '0.4px' }}>
                  24/7 Live Support Concierge
                </span>
              </div>

              {/* Main Headline */}
              <h2
                className="fw-bold mb-2 fs-2 fs-md-1"
                style={{ color: '#ffffff', letterSpacing: '-0.5px', lineHeight: '1.25' }}
              >
                Need help with your booking?
              </h2>

              {/* Subtitle */}
              <p
                className="mb-0 fs-6"
                style={{ color: '#94a3b8', maxWidth: '580px', lineHeight: '1.6' }}
              >
                Speak directly with our travel specialists for instant reservations, sacred temple circuits, group stays, or cancellation assistance.
              </p>

            </div>

            {/* Right Action Column */}
            <div className="col-12 col-lg-5">
              <div className="d-flex flex-column align-items-center align-items-lg-end gap-3">

                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2.5 w-100 justify-content-lg-end">

                  {/* Clean, High-Contrast White Call Button */}
                  <a
                    href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                    className="btn btn-light rounded-pill px-4 py-2.5 fw-bold d-inline-flex align-items-center justify-content-center gap-2 shadow-sm transition hover-lift text-decoration-none"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#0f172a',
                      fontSize: '15px',
                      border: 'none',
                      minHeight: '48px'
                    }}
                  >
                    <PhoneCall size={17} style={{ color: '#059669' }} />
                    <span className="fw-black">{phone}</span>
                  </a>

                  {/* Refined Translucent Contact Support Button */}
                  <Link
                    to="/contact-us"
                    className="btn btn-outline-light rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center justify-content-center gap-2 transition hover-lift text-decoration-none"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                      color: '#ffffff',
                      fontSize: '14.5px',
                      minHeight: '48px'
                    }}
                  >
                    <Mail size={16} />
                    <span>Contact Support</span>
                    <ArrowRight size={14} />
                  </Link>

                </div>

                {/* Minimalist Trust & Response Caption */}
                <div
                  className="d-flex align-items-center gap-2 text-center text-lg-end"
                  style={{ color: '#64748b', fontSize: '12px' }}
                >
                  <Clock size={13} style={{ color: '#94a3b8' }} />
                  <span>Average response time: <strong style={{ color: '#cbd5e1' }}>under 5 minutes</strong></span>
                  <span className="d-none d-sm-inline">•</span>
                  <span className="d-none d-sm-inline">Zero waiting time</span>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <style>{`
        .faq-item-card {
          transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
        }
        .faq-item-card:hover {
          border-color: #0d6efd !important;
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.08) !important;
        }
        .chevron-circle {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.25s ease, color 0.25s ease;
        }
        .rotate-180 {
          transform: rotate(180deg);
        }
        .hover-scale {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-scale:hover {
          transform: translateY(-2px);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .live-radar-dot {
          width: 9px;
          height: 9px;
          background-color: #10b981;
          border-radius: 50%;
          display: inline-block;
          box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 7px rgba(16, 185, 129, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
      `}</style>
    </>
  );
};

export default FaqSection;
